import React, {useMemo} from 'react';

import {DateRangeFilter} from 'src/openapi/admin/models/DateRangeFilter';
import {SavedListRequestSortField} from 'src/openapi/admin/models/SavedListRequest';
import {SavedListType} from 'src/openapi/admin/models/SavedListType';
import {SavedSearchRequestSortField} from 'src/openapi/admin/models/SavedSearchRequest';
import {SortDirection} from 'src/openapi/admin/models/SortDirection';

import BoxLoading from '@pb/kit-component-box-loading';
import Group from '@pb/kit-component-group';
import {StickyElement} from '@pb/kit-component-sticky-element';
import Typography from '@pb/kit-component-typography';

import {SavedTable} from '../components/saved-table';
import {SearchPanel} from '../components/search-panel';
import {ContentTabs} from '../content-tabs';
import {useGetSavedContentGeneralAlert} from '../hooks/useGetSavedContentGeneralAlert';
import {useGetSavedLists} from '../hooks/useGetSavedLists';
import {useGetSearchesEntityIdsCounts} from '../hooks/useGetSearchesEntityIdsCounts';
import {useGetSharedSavedLists} from '../hooks/useGetSharedSavedLists';
import {mergeSavedListsData} from '../utils/merge-lists-data';

import {container, savedSearchesPanel, sharedSearchesPanel} from './../content.module.styl';

type Props = {
    userId: number,
};

export const Lists = ({userId}: Props) => {
    const {
        data: savedLists,
        isLoading: savedListsLoading,
        currentPage: currentPageSavedLists,
        pageSize: pageSizeSavedLists,
        setCurrentPage: setCurrentPageSavedLists,
        setPageSize: setPageSizeSavedLists,
        searchQuery: savedSearchQuery,
        setSearchQuery: setSavedSearchQuery,

        // Sort and filter controls
        setSortField: setSavedSortField,
        setSortDirection: setSavedSortDirection,
        setTypeFilter: setSavedTypeFilter,
        setCreateDateFilter: setSavedCreateDateFilter,
        setLastRunDateFilter: setSavedLastRunDateFilter,
        setLastSharedDateFilter: setSavedLastSharedDateFilter,
        isEmptySearchCriteria: isEmptySavedListCriteria,
        totalCount: savedListsTotalCount,
    } = useGetSavedLists({
        userId,
    });

    const {
        data: sharedSavedLists,
        isLoading: sharedListsLoading,
        currentPage: currentPageSharedLists,
        pageSize: pageSizeSharedLists,
        setCurrentPage: setCurrentPageSharedLists,
        setPageSize: setPageSizeSharedLists,
        setSearchQuery: setSharedSearchQuery,
        searchQuery: sharedSearchQuery,
        // Sort and filter controls
        setSortField: setSharedSortField,
        setSortDirection: setSharedSortDirection,
        setTypeFilter: setSharedTypeFilter,
        setCreateDateFilter: setSharedCreateDateFilter,
        setLastRunDateFilter: setSharedLastRunDateFilter,
        setLastSharedDateFilter: setSharedLastSharedDateFilter,
        isEmptySearchCriteria: isEmptySharedListCriteria,
        totalCount: sharedListsTotalCount,
    } = useGetSharedSavedLists({
        userId,
    });

    const savedListIds = useMemo(() => {
        return Array.isArray(savedLists?.searches) ? savedLists.searches.map(search => search.id) : [];
    }, [savedLists]);

    const sharedListIds = useMemo(() => {
        return Array.isArray(sharedSavedLists?.searches) ? sharedSavedLists.searches.map(search => search.id) : [];
    }, [sharedSavedLists]);

    const {data: savedListsCountsData, isLoading: savedListsCountsLoading} = useGetSearchesEntityIdsCounts({
        userId,
        ids: savedListIds,
    });

    const {
        data: savedListsAlerts,
        isLoading: savedListsAlertsLoading,
    } = useGetSavedContentGeneralAlert({ids: savedListIds});

    const {data: sharedListsCountsData, isLoading: sharedListsCountsLoading} = useGetSearchesEntityIdsCounts({
        userId,
        ids: sharedListIds,
    });


    const {
        data: sharedListsAlerts,
        isLoading: sharedListsAlertsLoading,
    } = useGetSavedContentGeneralAlert({ids: sharedListIds});

    const changePageSizeSavedLists = (_currentPage: number, newPageSize: number) => {
        setCurrentPageSavedLists(1);
        setPageSizeSavedLists(newPageSize);
    };

    const changePageSizeSharedLists = (_currentPage: number, newPageSize: number) => {
        setCurrentPageSharedLists(1);
        setPageSizeSharedLists(newPageSize);
    };

    // map table sort field (used by headers) to list API sort field
    const mapSearchSortFieldToListSortField = (field: SavedSearchRequestSortField): SavedListRequestSortField => {
        switch (field) {
            case SavedSearchRequestSortField.CREATE_DATE:
                return SavedListRequestSortField.CREATE_DATE;
            case SavedSearchRequestSortField.LAST_RUN_DATE:
                return SavedListRequestSortField.LAST_RUN_DATE;
            case SavedSearchRequestSortField.LAST_SHARED_DATE:
                return SavedListRequestSortField.LAST_SHARED_DATE;
            case SavedSearchRequestSortField.SEARCH_TYPE:
                return SavedListRequestSortField.SEARCH_LIST_TYPE;
            case SavedSearchRequestSortField.NAME:
            default:
                return SavedListRequestSortField.NAME;
        }
    };

    // Handlers for saved lists sort and filter
    const handleSavedSortChange = (field: SavedSearchRequestSortField, direction: SortDirection) => {
        setSavedSortField(mapSearchSortFieldToListSortField(field));
        setSavedSortDirection(direction);
    };

    const handleSavedTypeFilterChange = (filter: SavedListType[]) => {
        setCurrentPageSavedLists(1);
        setSavedTypeFilter(filter);
    };

    const handleSavedDateFilterChange = (field: 'createDate' | 'lastRun' | 'lastShared', filter?: DateRangeFilter) => {
        setCurrentPageSavedLists(1);
        switch (field) {
            case 'createDate':
                setSavedCreateDateFilter(filter);
                break;
            case 'lastRun':
                setSavedLastRunDateFilter(filter);
                break;
            case 'lastShared':
                setSavedLastSharedDateFilter(filter);
                break;
        }
    };

    // Handlers for shared lists sort and filter
    const handleSharedSortChange = (field: SavedSearchRequestSortField, direction: SortDirection) => {
        setSharedSortField(mapSearchSortFieldToListSortField(field));
        setSharedSortDirection(direction);
    };

    const handleSharedTypeFilterChange = (filter: SavedListType[]) => {
        setCurrentPageSharedLists(1);
        setSharedTypeFilter(filter);
    };

    const handleSharedDateFilterChange = (field: 'createDate' | 'lastRun' | 'lastShared', filter?: DateRangeFilter) => {
        setCurrentPageSharedLists(1);
        switch (field) {
            case 'createDate':
                setSharedCreateDateFilter(filter);
                break;
            case 'lastRun':
                setSharedLastRunDateFilter(filter);
                break;
            case 'lastShared':
                setSharedLastSharedDateFilter(filter);
                break;
        }
    };

    return (

        <Group direction="v" gap="none">
            <StickyElement id="sticky-element">
                <ContentTabs userId={userId} activeTab="lists"/>
                <Group direction="h" gap="xxl" className={savedSearchesPanel}>
                    <Typography fontWeight="semi-bold" variant="heading-level-3">
                        Saved Lists {savedListsTotalCount ? `(${savedListsTotalCount})` : ''}
                    </Typography>
                    <SearchPanel
                        searchQuery={savedSearchQuery}
                        setSavedSearchQuery={setSavedSearchQuery}
                        typeLabel="List"
                        setCurrentPage={setCurrentPageSavedLists}
                    />
                </Group>
            </StickyElement>

            <BoxLoading loading={savedListsLoading || sharedListsLoading}>
                <Group direction="v" gap="m" className={container}>
                    <SavedTable
                        data={mergeSavedListsData(savedLists.searches, savedListsAlerts, savedListsCountsData)}
                        savedSearchType="LIST"
                        savedTableType="OWNER"
                        currentPage={currentPageSavedLists}
                        onPaginationChange={paginationData => {
                            setCurrentPageSavedLists(paginationData.currentPage);
                            setPageSizeSavedLists(paginationData.pageSize);
                        }}
                        totalCount={savedLists?.totalCount || 0}
                        onPageSizeChange={changePageSizeSavedLists}
                        userId={userId}
                        pageSize={pageSizeSavedLists}
                        onSortChange={handleSavedSortChange}
                        onListTypeFilterChange={handleSavedTypeFilterChange}
                        onDateFilterChange={handleSavedDateFilterChange}
                        isEmptySearchCriteria={isEmptySavedListCriteria}
                        isLoading={savedListsCountsLoading}
                        alertsLoading={savedListsAlertsLoading}
                    />

                    {(sharedSavedLists.searches.length > 0 || !isEmptySharedListCriteria) && (
                        <Group className="pt-l" direction="v" gap="m">
                            <StickyElement>
                                <Group direction="h" gap="xxl" className={sharedSearchesPanel}>
                                    <Typography fontWeight="semi-bold" variant="heading-level-3">
                                        Shared Lists {sharedListsTotalCount ? `(${sharedListsTotalCount})` : ''}
                                    </Typography>
                                    <SearchPanel
                                        searchQuery={sharedSearchQuery}
                                        setSharedSearchQuery={setSharedSearchQuery}
                                        typeLabel="List"
                                        setCurrentPage={setCurrentPageSharedLists}
                                        sectionLabel="Shared"
                                    />
                                </Group>
                            </StickyElement>

                            <SavedTable
                                data={mergeSavedListsData(
                                    sharedSavedLists.searches,
                                    sharedListsAlerts,
                                    sharedListsCountsData,
                                )}
                                savedSearchType="LIST"
                                savedTableType="SHARED"
                                currentPage={currentPageSharedLists}
                                onPaginationChange={paginationData => {
                                    setCurrentPageSharedLists(paginationData.currentPage);
                                    setPageSizeSharedLists(paginationData.pageSize);
                                }}
                                totalCount={sharedSavedLists?.totalCount || 0}
                                onPageSizeChange={changePageSizeSharedLists}
                                userId={userId}
                                pageSize={pageSizeSharedLists}
                                onSortChange={handleSharedSortChange}
                                onListTypeFilterChange={handleSharedTypeFilterChange}
                                onDateFilterChange={handleSharedDateFilterChange}
                                isEmptySearchCriteria={isEmptySharedListCriteria}
                                isLoading={sharedListsCountsLoading}
                                alertsLoading={sharedListsAlertsLoading}
                            />
                        </Group>
                    )}
                </Group>
            </BoxLoading>
        </Group>

    );
};
